import * as R from 'ramda';
import ScriptTag from 'react-script-tag';
import { useDispatch } from 'react-redux';
import { RouteError } from './components/route-error';
import React, { Fragment, useEffect, Suspense } from 'react';
import {
  Outlet,
  redirect,
  useNavigate,
  useLocation,
  RouterProvider,
  createBrowserRouter,
} from 'react-router';
// helpers/constants
import * as GC from './constants';
import History from './helpers/route';
import { detectDevice } from './helpers/window';
// ui
import { Box, ListWrapper } from './ui';
// utilities
import routesMap from './utilities/routes';
import { lazyWithRetry } from './utilities/lazy-with-retry';
// features
import Login from './features/auth/login';
import OAuthPopup from './features/oauth-popup';
import NALLogin from './features/auth/nal-login';
import MainLayout from './components/main-layout';
import BoshLosRedirect from './features/auth/bosh-los-redirect';
import BoshLosCallback from './features/auth/bosh-los-callback';
import AzureSsoRedirect from './features/auth/azure-sso-redirect';
import AzureSsoCallback from './features/auth/azure-sso-callback';
import OrderListComponent from './features/dispatch-report/order';
import { visitPage as visitDriverCardsV2Page } from './features/driver-cards-v2/actions';
import UserTermsAndConditions from './features/auth/legal-info/user-terms-and-conditions';
import { visitPage as visitCentralizedDispatchPage } from './features/centralized-dispatch/actions';
//////////////////////////////////////////////////

// general
const LoadBoard = lazyWithRetry(() => import('./features/load-board'), true);
const DashboardComponent = lazyWithRetry(() => import('./features/dashboard'), true);
const DashboardsComponent = lazyWithRetry(() => import('./features/dashboards'), true);
const CustomerPortal = lazyWithRetry(() => import('./features/customer-portal'), true);
const DriversCardComponent = lazyWithRetry(() => import('./features/drivers-card'), true);
const DriverCardsV2Component = lazyWithRetry(() => import('./features/driver-cards-v2'), true);
const AvailableDriverList = lazyWithRetry(() => import('./features/available-driver'), true);
const CentralizedDispatch = lazyWithRetry(() => import('./features/centralized-dispatch'), true);
// dispatch
const RouteListLoad = lazyWithRetry(() => import('./features/dispatch-board/load'), true);
const RouteListOrder = lazyWithRetry(() => import('./features/dispatch-board/order'), true);
const LoadDetailsNewComponent = lazyWithRetry(() => import('./features/dispatch-details/load'), true);
const OrderDetailsNewComponent = lazyWithRetry(() => import('./features/dispatch-details/order'), true);
// invoice
const DriverPayrollsComponent = lazyWithRetry(() => import('./features/payroll'), true);
const CustomerMasterInvoicesComponent = lazyWithRetry(() => import('./features/master-invoice'), true);
// templates
const TemplateInspection = lazyWithRetry(() => import('./features/template/inspection'), true);
// views
const MasterAudit = lazyWithRetry(() => import('./features/master-audit'), true);
const CloItemsComponent = lazyWithRetry(() => import('./features/clo-items'), true);
const LoadListComponent = lazyWithRetry(() => import('./features/dispatch-report/load'), true);
const RouteListComponent = lazyWithRetry(() => import('./features/dispatch-report/route'), true);
// fleet
const FleetList = lazyWithRetry(() => import('./features/fleet-list'), true);
const FuelCardListComponent = lazyWithRetry(() => import('./features/fuel-cards'), true);

const startMouseFlow = () => {
  window._mfq = window._mfq || [];

  return '//cdn.mouseflow.com/projects/90f2fef0-3549-408a-8856-8f627e775da9.js';
};

const Fallback = () => <Box height='100vh' bg='bgGrey' />;

detectDevice();

const Root = ({ withoutLayout }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  History.location = location;
  History.navigate = navigate;

  useEffect(() => {
    dispatch({ payload: location, type: GC.LOCATION_CHANGE_ACTION });
  }, [location]);

  if (withoutLayout) return null;

  const { pathname } = location;

  const preparedPath = pathname.replace(GC.GUID_REPLACEMENT_REGEX, ':guid');

  const isMobileViewport = R.and(window.isNotDesktop, R.includes(preparedPath, GC.MOBILE_VIEWPORT_ROUTE_MAP));

  return (
    <Fragment>
      { isMobileViewport && (
        <Fragment>
          <meta name='format-detection' content='telephone=no' />
          <meta name='viewport' content='width=device-width, initial-scale=1, viewport-fit=cover' />
        </Fragment>
      )}
      <MainLayout />
      <ScriptTag type='text/javascript' src={startMouseFlow()} />
    </Fragment>
  );
};

const RootElement = () => {
  const location = useLocation();

  return (
    <Fragment>
      <Root />
      <Suspense key={location.key} fallback={<Fallback />}>
        <Outlet />
      </Suspense>
    </Fragment>
  );
};

const routes2 = (dispatch) => [
  {
    path: '*',
    element: <Root />,
  },
  {
    errorElement: <RouteError />,
    hydrateFallbackElement: <Fallback />,
    element: (
      <Fragment>
        <Root withoutLayout={true} />
        <Outlet />
      </Fragment>
    ),
    children: [
      {
        Component: Login,
        path: GC.ROUTE_PATH_LOGIN,
      },
      {
        Component: UserTermsAndConditions,
        path: GC.ROUTE_PATH_USER_TERMS_AND_CONDITIONS,
      },
      {
        Component: NALLogin,
        path: GC.ROUTE_PATH_NAL_LOGIN,
        loader: () => {
          dispatch({ payload: {}, type: GC.VISIT_NAL_LOGIN_PAGE });

          return null;
        },
      },
      {
        Component: AzureSsoRedirect,
        path: GC.ROUTE_PATH_AZURE_SSO_REDIRECT,
        loader: () => {
          dispatch({ type: GC.VISIT_AZURE_SSO_REDIRECT_PAGE });

          return null;
        },
      },
      {
        Component: AzureSsoCallback,
        path: GC.ROUTE_PATH_AZURE_SSO_CALLBACK,
        loader: () => {
          dispatch({ type: GC.VISIT_AZURE_SSO_CALLBACK_PAGE });

          return null;
        },
      },
      {
        path: GC.ROUTE_PATH_NEW_PASSWORD,
        lazy: lazyWithRetry(() => import('./features/auth/new-password')),
      },
      {
        path: GC.ROUTE_PATH_RESET_PASSWORD,
        lazy: lazyWithRetry(() => import('./features/auth/reset-password')),
      },
      {
        path: GC.ROUTE_PATH_TERMS_AND_CONDITIONS,
        lazy: lazyWithRetry(() => import('./features/auth/legal-info/terms-and-conditions')),
      },
      {
        path: GC.ROUTE_PATH_PRIVACY_POLICY,
        lazy: lazyWithRetry(() => import('./features/auth/legal-info/privacy-policy')),
      },
      {
        path: GC.ROUTE_PATH_NORTHERN_DRIVER_PRIVACY_NOTICE,
        lazy: lazyWithRetry(() => import('./features/auth/legal-info/northerntms/driver-privacy-notice')),
      },
      {
        path: GC.ROUTE_PATH_NORTHERN_CARRIER_TRACKING_PRIVACY_NOTICE,
        lazy: lazyWithRetry(() => import('./features/auth/legal-info/northerntms/carrier-tracking-privacy-notice')),
      },
      {
        path: GC.ROUTE_PATH_NEW_PASSWORD_MOBILE,
        lazy: lazyWithRetry(() => import('./features/auth/new-password-mobile')),
      },
      {
        path: GC.ROUTE_PATH_NEW_PASSWORD_MOBILE_SUCCESS_PAGE,
        lazy: lazyWithRetry(() => import('./features/auth/new-password-mobile-success-page')),
      },
      {
        path: GC.ROUTE_PATH_CARRIERS_RESPONSE,
        lazy: lazyWithRetry(() => import('./features/carrier-portal/response-page')),
      },
      {
        path: GC.ROUTE_PATH_CARRIERS_ACCEPT,
        lazy: lazyWithRetry(() => import('./features/carrier-portal/carrier-accept')),
        // example
        // loader: ({ params }) => {
        //   dispatch({ payload: {}, type: GC.VISIT_PORTAL_ACCEPT_PAGE });

        //   return null;
        // },
      },
      {
        path: GC.ROUTE_PATH_CARRIERS_DECLINE,
        lazy: lazyWithRetry(() => import('./features/carrier-portal/carrier-decline')),
      },
      {
        path: GC.ROUTE_PATH_ORDER_QUOTE_ACCEPT,
        lazy: lazyWithRetry(() => import('./features/carrier-portal/carrier-accept')),
      },
      {
        path: GC.ROUTE_PATH_ORDER_QUOTE_DECLINE,
        lazy: lazyWithRetry(() => import('./features/carrier-portal/carrier-decline')),
      },
      {
        path: GC.ROUTE_PATH_CARRIER_QUOTE_ACCEPT,
        lazy: lazyWithRetry(() => import('./features/carrier-portal/carrier-accept')),
      },
      {
        path: GC.ROUTE_PATH_CARRIER_QUOTE_DECLINE,
        lazy: lazyWithRetry(() => import('./features/carrier-portal/carrier-decline')),
      },
      {
        path: GC.ROUTE_PATH_CARRIERS_ADD_MESSAGE_PAGE,
        lazy: lazyWithRetry(() => import('./features/carrier-portal')),
      },
      {
        Component: BoshLosRedirect,
        path: GC.ROUTE_PATH_BOSH_LOS_REDIRECT,
        loader: () => {
          dispatch({ type: GC.VISIT_BOSH_REDIRECT_PAGE });
        },
      },
      {
        element: <BoshLosCallback />,
        path: GC.ROUTE_PATH_BOSH_LOS_CALLBACK,
        loader: () => {
          dispatch({ type: GC.VISIT_BOSH_CALLBACK_PAGE });

          return null;
        },
      },
    ],
  },
  {
    element: <RootElement />,
    errorElement: <RouteError />,
    hydrateFallbackElement: <Fallback />,
    children: [
      {
        path: GC.ROUTE_PATH_DO_NEW,
        lazy: lazyWithRetry(() => import('./features/new-do')),
        loader: () => dispatch({
          type: GC.VISIT_NEW_DO_PAGE,
          payload: GC.ROUTE_PATH_DO_NEW,
        }),
      },
      {
        path: GC.ROUTE_PATH_DO_NEW_FROM_BY_SOURCE_TYPE,
        lazy: lazyWithRetry(() => import('./features/new-do')),
        loader: ({ params }) => dispatch({
          type: GC.VISIT_NEW_DO_PAGE_BY_SOURCE_TYPE,
          payload: {
            ...params,
            route: GC.ROUTE_PATH_DO_NEW_FROM_BY_SOURCE_TYPE,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_LITE_DO_NEW_BY_SOURCE_TYPE,
        lazy: lazyWithRetry(() => import('./features/lite-new-do')),
        loader: ({ params }) => dispatch({
          type: GC.VISIT_LITE_NEW_DO_PAGE_BY_SOURCE_TYPE,
          payload: {
            ...params,
            route: GC.ROUTE_PATH_LITE_DO_NEW_BY_SOURCE_TYPE,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_DO_QUOTE,
        lazy: lazyWithRetry(() => import('./features/new-do-quote')),
        loader: () => dispatch({
          payload: GC.ROUTE_PATH_DO_QUOTE,
          type: GC.VISIT_NEW_DO_QUOTE_PAGE,
        }),
      },
      {
        path: GC.ROUTE_PATH_LITE_DO_NEW,
        lazy: lazyWithRetry(() => import('./features/lite-new-do')),
        loader: () => dispatch({
          type: GC.VISIT_LITE_NEW_DO_PAGE,
          payload: GC.ROUTE_PATH_LITE_DO_NEW,
        }),
      },
      {
        path: GC.ROUTE_PATH_DASHBOARDS,
        Component: DashboardsComponent,
        loader: () => dispatch({
          type: GC.VISIT_DASHBOARDS_PAGE,
          payload: {
            pathname: GC.ROUTE_PATH_DASHBOARDS,
          },
        }),
      },
      {
        Component: DashboardComponent,
        path: GC.ROUTE_PATH_ANALYTICS,
        loader: () => dispatch({
          type: GC.VISIT_ANALYTICS_PAGE,
          payload: {
            pathname: GC.ROUTE_PATH_ANALYTICS,
          },
        }),
      },
      {
        Component: CustomerPortal,
        path: GC.ROUTE_PATH_CUSTOMER_PORTAL,
        loader: () => dispatch({
          type: GC.VISIT_CUSTOMER_PORTAL_PAGE,
          payload: {
            pathname: GC.ROUTE_PATH_CUSTOMER_PORTAL,
          },
        }),
      },
      {
        Component: DriversCardComponent,
        path: GC.ROUTE_PATH_DRIVERS_CARD,
        loader: () => dispatch({
          type: GC.VISIT_DRIVERS_CARD_PAGE,
          payload: {
            pathname: GC.ROUTE_PATH_DRIVERS_CARD,
          },
        }),
      },
      {
        Component: DriverCardsV2Component,
        path: GC.ROUTE_PATH_DRIVER_CARDS_V2,
        loader: () => dispatch(visitDriverCardsV2Page({
          pathname: GC.ROUTE_PATH_DRIVER_CARDS_V2,
        })),
      },
      {
        loader: () => dispatch({
          payload: {
            pathname: GC.ROUTE_PATH_AVAILABLE_DRIVER_LIST,
          },
          type: GC.VISIT_ROUTE_AVAILABLE_DRIVER_PAGE,
        }),
        Component: AvailableDriverList,
        path: GC.ROUTE_PATH_AVAILABLE_DRIVER_LIST,
      },
      {
        Component: CentralizedDispatch,
        path: GC.ROUTE_PATH_CENTRALIZED_DISPATCH,
        loader: () => dispatch(visitCentralizedDispatchPage({
          pathname: GC.ROUTE_PATH_CENTRALIZED_DISPATCH,
        })),
      },
      {
        loader: () => dispatch({
          payload: {
            route: GC.ROUTE_PATH_LOAD_PLANNER,
          },
          type: GC.VISIT_LOAD_PLANNER_V2_PAGE,
        }),
        path: GC.ROUTE_PATH_LOAD_PLANNER,
        lazy: lazyWithRetry(() => import('./features/dispatch-planner-events-v2')),
      },
      {
        path: GC.ROUTE_PATH_IMPORT,
        lazy: lazyWithRetry(() => import('./features/new-import')),
        loader: () => dispatch({
          type: GC.VISIT_IMPORT_PAGE,
          payload: {
            pathname: GC.ROUTE_PATH_IMPORT,
          },
        }),
      },
      {
        Component: LoadBoard,
        path: GC.ROUTE_PATH_LOAD_BOARD,
        loader: () => dispatch({
          type: GC.VISIT_LOAD_BOARD_PAGE,
          payload: {
            pathname: GC.ROUTE_PATH_LOAD_BOARD,
          },
        }),
      },
      // dispatch
      {
        Component: RouteListLoad,
        path: GC.ROUTE_PATH_DISPATCH_BOARD_TEL,
        loader: () => dispatch({
          type: GC.VISIT_DISPATCH_BOARD_TEL_PAGE,
          payload: {
            route: GC.ROUTE_PATH_DISPATCH_BOARD_TEL,
          },
        }),
      },
      {
        Component: RouteListOrder,
        path: GC.ROUTE_PATH_DISPATCH_BOARD_CLO,
        loader: () => dispatch({
          type: GC.VISIT_DISPATCH_BOARD_CLO_PAGE,
          payload: {
            route: GC.ROUTE_PATH_DISPATCH_BOARD_CLO,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_ROUTE_LOAD,
        Component: LoadDetailsNewComponent,
        loader: ({ params }) => dispatch({
          type: GC.VISIT_DISPATCH_DETAILS_LOAD_PAGE,
          payload: {
            ...params,
            route: GC.ROUTE_PATH_ROUTE_LOAD,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_ROUTE_ORDER,
        Component: OrderDetailsNewComponent,
        loader: ({ params }) => dispatch({
          type: GC.VISIT_DISPATCH_DETAILS_ORDER_PAGE,
          payload: {
            ...params,
            route: GC.ROUTE_PATH_ROUTE_ORDER,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_EDI_LIST,
        lazy: lazyWithRetry(() => import('./features/edi-integration')),
        loader: () => dispatch({
          type: GC.VISIT_EDI_LIST_PAGE,
          payload: {
            reportType: GC.EDI_REPORT,
            pathname: GC.ROUTE_PATH_EDI_LIST,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_CARRIER_EDI_LIST,
        lazy: lazyWithRetry(() => import('./features/carrier-edi')),
        loader: () => dispatch({
          type: GC.VISIT_CARRIER_EDI_LIST_PAGE,
          payload: {
            reportType: GC.INVOICE_EDI_REPORT,
            pathname: GC.ROUTE_PATH_CARRIER_EDI_LIST,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_PUBLIC_CLO_LIST,
        lazy: lazyWithRetry(() => import('./features/public-clo')),
        loader: () => dispatch({
          type: GC.VISIT_PUBLIC_CLO_LIST_PAGE,
          payload: {
            reportType: GC.ORDER_REQUEST_REPORT,
            pathname: GC.ROUTE_PATH_PUBLIC_CLO_LIST,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_QUOTE,
        lazy: lazyWithRetry(() => import('./features/quotes')),
        loader: () => dispatch({ type: GC.VISIT_QUOTES_LIST_PAGE }),
      },
      {
        path: GC.ROUTE_PATH_ORDER_QUOTES,
        lazy: lazyWithRetry(() => import('./features/quotes')),
        loader: () => dispatch({ type: GC.VISIT_ORDER_QUOTES_LIST_PAGE }),
      },
      // invoice
      {
        element: (
          <ListWrapper>
            <Outlet />
          </ListWrapper>
        ),
        children: [
          {
            path: GC.ROUTE_PATH_DRIVER_INVOICES_LIST,
            lazy: lazyWithRetry(() => import('./features/invoice/driver')),
            loader: () => dispatch({
              type: GC.VISIT_INVOICE_DRIVER_PAYROLL_LIST_PAGE,
              payload: {
                pathname: GC.ROUTE_PATH_DRIVER_INVOICES_LIST,
                reportType: GC.DRIVER_PAYROLL_INVOICE_REPORT,
              },
            }),
          },
          {
            path: GC.ROUTE_PATH_VENDOR_INVOICES_LIST,
            lazy: lazyWithRetry(() => import('./features/invoice/vendor')),
            loader: () => dispatch({
              type: GC.VISIT_INVOICE_VENDOR_LIST_PAGE,
              payload: {
                reportType: GC.VENDOR_INVOICE_REPORT,
                pathname: GC.ROUTE_PATH_VENDOR_INVOICES_LIST,
              },
            }),
          },
          {
            path: GC.ROUTE_PATH_CUSTOMER_INVOICES_LIST,
            lazy: lazyWithRetry(() => import('./features/invoice/customer')),
            loader: () => dispatch({
              type: GC.VISIT_INVOICE_CUSTOMER_LIST_PAGE,
              payload: {
                reportType: GC.CUSTOMER_INVOICE_REPORT,
                pathname: GC.ROUTE_PATH_CUSTOMER_INVOICES_LIST,
              },
            }),
          },
          {
            path: GC.ROUTE_PATH_CARRIER_INVOICES_LIST,
            lazy: lazyWithRetry(() => import('./features/invoice/carrier')),
            loader: () => dispatch({
              type: GC.VISIT_INVOICE_CARRIER_LIST_PAGE,
              payload: {
                reportType: GC.CARRIER_INVOICE_REPORT,
                pathname: GC.ROUTE_PATH_CARRIER_INVOICES_LIST,
              },
            }),
          },
          {
            path: GC.ROUTE_PATH_SERVICE_VENDOR_INVOICE_LIST,
            lazy: lazyWithRetry(() => import('./features/invoice/service-vendor')),
            loader: () => dispatch({
              type: GC.VISIT_INVOICE_SERVICE_VENDOR_LIST_PAGE,
              payload: {
                reportType: GC.SERVICE_VENDOR_INVOICE_REPORT,
                pathname: GC.ROUTE_PATH_SERVICE_VENDOR_INVOICE_LIST,
              },
            }),
          },
        ],
      },
      {
        Component: CustomerMasterInvoicesComponent,
        path: GC.ROUTE_PATH_CUSTOMER_MASTER_INVOICE_LIST,
        loader: () => dispatch({
          type: GC.VISIT_CUSTOMER_MASTER_INVOICES_PAGE,
          payload: {
            reportType: GC.CUSTOMER_MASTER_INVOICE_REPORT,
            pathname: GC.ROUTE_PATH_CUSTOMER_MASTER_INVOICE_LIST,
          },
        }),
      },
      {
        Component: DriverPayrollsComponent,
        path: GC.ROUTE_PATH_DRIVER_PAYROLL_LIST,
        loader: () => dispatch({
          type: GC.VISIT_DRIVER_PAYROLL_PAGE,
          payload: {
            reportType: GC.PAYROLL_REPORT,
            pathname: GC.ROUTE_PATH_DRIVER_PAYROLL_LIST,
          },
        }),
      },
      // contracts
      {
        path: GC.ROUTE_PATH_CARRIER_CONTRACTS,
        lazy: lazyWithRetry(() => import('./features/contracts/carrier')),
        loader: () => dispatch({
          type: GC.VISIT_CARRIER_CONTRACTS_PAGE,
          payload: {
            reportType: GC.CARRIER_CONTRACT_REPORT,
            pathname: GC.ROUTE_PATH_CARRIER_CONTRACTS,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_CUSTOMER_CONTRACTS,
        lazy: lazyWithRetry(() => import('./features/contracts/customer')),
        loader: () => dispatch({
          type: GC.VISIT_CUSTOMER_CONTRACTS_PAGE,
          payload: {
            reportType: GC.CUSTOMER_CONTRACT_REPORT,
            pathname: GC.ROUTE_PATH_CUSTOMER_CONTRACTS,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_CARRIER_CONTRACT_DETAILS,
        lazy: lazyWithRetry(() => import('./features/carrier-profile/contracts/carrier')),
        loader: ({ params }) => dispatch({
          type: GC.VISIT_CARRIER_CONTRACT_DETAILS_PAGE,
          payload: {
            ...params,
            pathname: GC.ROUTE_PATH_CARRIER_CONTRACT_DETAILS,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_CUSTOMER_CONTRACT_DETAILS,
        lazy: lazyWithRetry(() => import('./features/carrier-profile/contracts/customer')),
        loader: ({ params }) => dispatch({
          type: GC.VISIT_CUSTOMER_CONTRACT_DETAILS_PAGE,
          payload: {
            ...params,
            pathname: GC.ROUTE_PATH_CUSTOMER_CONTRACT_DETAILS,
          },
        }),
      },
      // templates
      {
        path: GC.ROUTE_PATH_ITEMS_LIST,
        lazy: lazyWithRetry(() => import('./features/template/report')),
        loader: () => dispatch({
          type: GC.VISIT_ITEMS_LIST_PAGE,
          payload: {
            listType: 'listUp',
            reportType: GC.ITEM_REPORT,
            pathname: GC.ROUTE_PATH_ITEMS_LIST,
            checkVisitPage: GC.CHECK_VISIT_ITEMS_LIST_PAGE,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_LOCATIONS_LIST,
        lazy: lazyWithRetry(() => import('./features/template/report')),
        loader: () => dispatch({
          type: GC.VISIT_LOCATIONS_LIST_PAGE,
          payload: {
            listType: 'listUp',
            pathname: GC.ROUTE_PATH_LOCATIONS_LIST,
            reportType: GC.LOCATION_TEMPLATE_REPORT,
            checkVisitPage: GC.CHECK_VISIT_LOCATIONS_LIST_PAGE,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_TEMPLATES_EQUIPMENT_SERVICE_LIST,
        lazy: lazyWithRetry(() => import('./features/template/report')),
        loader: () => dispatch({
          type: GC.VISIT_EQUIPMENT_SERVICE_TEMPLATE_LIST_PAGE,
          payload: {
            listType: 'listUp',
            reportType: GC.EQUIPMENT_SERVICE_TEMPLATE_REPORT,
            pathname: GC.ROUTE_PATH_TEMPLATES_EQUIPMENT_SERVICE_LIST,
            checkVisitPage: GC.CHECK_VISIT_EQUIPMENT_SERVICE_TEMPLATE_LIST_PAGE,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_CONTACT_BOOK,
        lazy: lazyWithRetry(() => import('./features/template/report')),
        loader: () => dispatch({
          type: GC.VISIT_CONTACT_BOOK_PAGE,
          payload: {
            listType: 'listUp',
            reportType: GC.CONTACT_BOOK_REPORT,
            pathname: GC.ROUTE_PATH_CONTACT_BOOK,
            checkVisitPage: GC.CHECK_VISIT_CONTACT_BOOK_PAGE,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_SHARED_ACCESSORIALS,
        lazy: lazyWithRetry(() => import('./features/template/report')),
        loader: () => dispatch({
          type: GC.VISIT_SHARED_ACCESORIALS_PAGE,
          payload: {
            listType: 'list',
            reportType: GC.SHARED_ACCESSORIAL_REPORT,
            pathname: GC.ROUTE_PATH_SHARED_ACCESSORIALS,
            checkVisitPage: GC.CHECK_VISIT_CONTACT_BOOK_PAGE,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_TEMPLATES_DO_LIST,
        lazy: lazyWithRetry(() => import('./features/template/report')),
        loader: () => dispatch({
          type: GC.VISIT_DO_TEMPLATE_LIST_PAGE,
          payload: {
            listType: 'list',
            reportType: GC.CLO_TEMPLATE_REPORT,
            pathname: GC.ROUTE_PATH_TEMPLATES_DO_LIST,
            checkVisitPage: GC.CHECK_VISIT_DO_TEMPLATE_LIST_PAGE,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_TEMPLATE_DO_DETAILS,
        lazy: lazyWithRetry(() => import('./features/new-do')),
        loader: ({ params }) => dispatch({
          type: GC.VISIT_TEMPLATE_DETAILS_PAGE,
          payload: {
            ...params,
            route: GC.ROUTE_PATH_TEMPLATE_DO_DETAILS,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_LITE_TEMPLATE_DO_DETAILS,
        lazy: lazyWithRetry(() => import('./features/lite-new-do')),
        loader: ({ params }) => dispatch({
          type: GC.VISIT_LITE_TEMPLATE_DETAILS_PAGE,
          payload: {
            ...params,
            route: GC.ROUTE_PATH_LITE_TEMPLATE_DO_DETAILS,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_TEMPLATE_CONTAINER_LIST,
        lazy: lazyWithRetry(() => import('./features/template/report')),
        loader: () => dispatch({
          type: GC.VISIT_CONTAINER_TEMPLATE_LIST_PAGE,
          payload: {
            listType: 'listUp',
            reportType: GC.CONTAINER_TEMPLATE_REPORT,
            pathname: GC.ROUTE_PATH_TEMPLATE_CONTAINER_LIST,
            checkVisitPage: GC.CHECK_VISIT_CONTAINER_TEMPLATE_LIST_PAGE,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_TEMPLATES_ROUTE_LIST,
        lazy: lazyWithRetry(() => import('./features/template/report')),
        loader: () => dispatch({
          type: GC.VISIT_ROUTE_TEMPLATE_LIST_PAGE,
          payload: {
            listType: 'list',
            reportType: GC.ROUTE_TEMPLATE_REPORT,
            pathname: GC.ROUTE_PATH_TEMPLATES_ROUTE_LIST,
            checkVisitPage: GC.CHECK_VISIT_ROUTE_TEMPLATE_LIST_PAGE,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_TEMPLATES_COMPENSATION_LIST,
        lazy: lazyWithRetry(() => import('./features/template/report')),
        loader: () => dispatch({
          type: GC.VISIT_COMPENSATION_TEMPLATE_LIST_PAGE,
          payload: {
            listType: 'listUp',
            reportType: GC.COMPENSATION_TEMPLATE_REPORT,
            pathname: GC.ROUTE_PATH_TEMPLATES_COMPENSATION_LIST,
            checkVisitPage: GC.CHECK_VISIT_COMPENSATION_TEMPLATE_LIST_PAGE,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_CONTAINER_TYPE_LIST,
        lazy: lazyWithRetry(() => import('./features/template/container-type')),
        loader: () => dispatch({
          type: GC.VISIT_CONTAINER_TYPE_LIST_PAGE,
          payload: {
            pathname: GC.ROUTE_PATH_CONTAINER_TYPE_LIST,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_COMMISSION_ASSIGNMENT,
        lazy: lazyWithRetry(() => import('./features/commission-assignment')),
        loader: () => dispatch({
          type: GC.VISIT_COMMISSION_ASSIGNMENT_PAGE,
          payload: {
            pathname: GC.ROUTE_PATH_COMMISSION_ASSIGNMENT,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_GEO_FENCING_ZONE,
        lazy: lazyWithRetry(() => import('./features/geo-fencing-zone/rating')),
        loader: () => dispatch({
          type: GC.VISIT_RATING_GEO_FENCING_ZONE_PAGE,
          payload: {
            pathname: GC.ROUTE_PATH_GEO_FENCING_ZONE,
            reportType: GC.RATING_GEO_FENCING_ZONE_REPORT,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_GEO_FENCING_ZONE_REPORT,
        lazy: lazyWithRetry(() => import('./features/geo-fencing-zone/report')),
        loader: () => dispatch({
          type: GC.VISIT_REPORT_GEO_FENCING_ZONE_PAGE,
          payload: {
            reportType: GC.REPORT_GEO_FENCING_ZONE_REPORT,
            pathname: GC.ROUTE_PATH_GEO_FENCING_ZONE_REPORT,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_LOAD_PLANNER_TEMPLATE_LIST,
        lazy: lazyWithRetry(() => import('./features/load-planner-template')),
        loader: () => dispatch({
          type: GC.VISIT_LOAD_PLANNER_TEMPLATE_LIST_PAGE,
          payload: {
            listType: 'list',
            pathname: GC.ROUTE_PATH_LOAD_PLANNER_TEMPLATE_LIST,
            checkVisitPage: GC.CHECK_VISIT_LOAD_PLANNER_TEMPLATE_LIST_PAGE,
          },
        }),
      },
      {
        Component: TemplateInspection,
        path: GC.ROUTE_PATH_TEMPLATES_INSPECTION,
        loader: () => dispatch({
          type: GC.VISIT_TEMPLATE_INSPECTION_PAGE,
          payload: {
            pathname: GC.ROUTE_PATH_TEMPLATES_INSPECTION,
          },
        }),
      },
      // views
      {
        path: GC.ROUTE_PATH_USERS_LIST,
        lazy: lazyWithRetry(() => import('./features/user')),
        loader: () => {
          dispatch({ type: GC.VISIT_USERS_LIST_PAGE });

          return null;
        },
      },
      {
        path: GC.ROUTE_PATH_USERS_SETTINGS,
        lazy: lazyWithRetry(() => import('./features/profile')),
        loader: ({ params }) => {
          dispatch({
            type: GC.VISIT_USER_EDIT_PAGE,
            payload: { ...params, route: GC.ROUTE_PATH_USERS_SETTINGS },
          });

          return null;
        },
      },
      {
        path: GC.ROUTE_PATH_PROFILE,
        lazy: lazyWithRetry(() => import('./features/profile')),
        loader: () => {
          dispatch({ type: GC.VISIT_CURRENT_USER_PROFILE });

          return null;
        },
      },
      {
        path: GC.ROUTE_PATH_ROLES_LIST,
        lazy: lazyWithRetry(() => import('./features/role')),
        loader: () => {
          dispatch({ type: GC.VISIT_ROLES_LIST_PAGE, payload: GC.ROUTE_PATH_ROLES_LIST });

          return null;
        },
      },
      {
        path: GC.ROUTE_PATH_ROLES_EDIT,
        lazy: lazyWithRetry(() => import('./features/role/components/edit-role')),
        loader: ({ params }) => dispatch({
          type: GC.VISIT_ROLE_EDIT_PAGE,
          payload: { ...params, route: GC.ROUTE_PATH_ROLES_EDIT },
        }),
      },
      {
        path: GC.ROUTE_PATH_ROLES_NEW,
        lazy: lazyWithRetry(() => import('./features/role/components/create-role')),
        loader: () => dispatch({
          type: GC.VISIT_ROLE_CREATE_PAGE,
          payload: GC.ROUTE_PATH_ROLES_NEW,
        }),
      },
      {
        path: GC.ROUTE_PATH_CARRIERS_LIST,
        lazy: lazyWithRetry(() => import('./features/carrier')),
        loader: () => {
          dispatch({
            type: GC.VISIT_CARRIER_LIST_PAGE,
            payload: {
              reportType: GC.CARRIER_REPORT,
              pathname: GC.ROUTE_PATH_CARRIERS_LIST,
            },
          });

          return null;
        },
      },
      {
        element: <div />,
        path: GC.ROUTE_PATH_CARRIER_EDIT,
        loader: ({ params: { guid } = {} }) => {
          throw redirect(routesMap.carrierProfile(guid));
        },
      },
      {
        path: GC.ROUTE_PATH_CARRIER_PROFILE,
        lazy: lazyWithRetry(() => import('./features/carrier-profile')),
        loader: ({ params }) => {
          dispatch({ type: GC.VISIT_CARRIER_PROFILE_PAGE, payload: params });

          return null;
        },
      },
      {
        path: GC.ROUTE_PATH_SERVICE_VENDOR_LIST,
        lazy: lazyWithRetry(() => import('./features/service-vendor')),
        loader: () => dispatch({
          type: GC.VISIT_SERVICE_VENDOR_LIST_PAGE,
          payload: {
            reportType: GC.SERVICE_VENDOR_REPORT,
            pathname: GC.ROUTE_PATH_SERVICE_VENDOR_LIST,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_SERVICE_VENDOR_EDIT,
        lazy: lazyWithRetry(() => import('./features/service-vendor/components/edit-form')),
        loader: ({ params }) => dispatch({
          type: GC.VISIT_SERVICE_VENDOR_EDIT_PAGE,
          payload: {
            ...params,
            pathname: GC.ROUTE_PATH_SERVICE_VENDOR_EDIT,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_BRANCH_LIST,
        lazy: lazyWithRetry(() => import('./features/branch')),
        loader: () => dispatch({
          type: GC.VISIT_BRANCH_LIST_PAGE,
          payload: {
            reportType: GC.BRANCH_REPORT,
            pathname: GC.ROUTE_PATH_BRANCH_LIST,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_BRANCH_EDIT,
        lazy: lazyWithRetry(() => import('./features/branch/components/edit-branch')),
        loader: ({ params }) => dispatch({
          type: GC.VISIT_BRANCH_EDIT_PAGE,
          payload: {
            ...params,
            route: GC.ROUTE_PATH_BRANCH_EDIT,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_SEQUENCES_LIST,
        lazy: lazyWithRetry(() => import('./features/sequence')),
        loader: () => dispatch({ type: GC.VISIT_SEQUENCES_LIST_PAGE, payload: GC.ROUTE_PATH_SEQUENCES_LIST }),
      },
      {
        path: GC.ROUTE_PATH_REFERENCES_LIST,
        lazy: lazyWithRetry(() => import('./features/reference')),
        loader: () => dispatch({
          payload: GC.ROUTE_PATH_REFERENCES_LIST,
          type: GC.VISIT_REFERENCE_TYPES_LIST_PAGE,
        }),
      },
      {
        path: GC.ROUTE_PATH_REPORTS_LIST,
        lazy: lazyWithRetry(() => import('./features/report-format')),
        loader: () => dispatch({
          type: GC.VISIT_REPORTS_LIST_PAGE,
          payload: {
            reportType: GC.REPORT_REPORT,
            pathname: GC.ROUTE_PATH_REPORTS_LIST,
          },
        }),
      },
      {
        Component: OrderListComponent,
        path: GC.ROUTE_PATH_CLO_REPORT_LIST,
        loader: () => dispatch({
          type: GC.VISIT_CLO_LIST_PAGE,
          payload: {
            reportType: GC.CLO_REPORT,
            pathname: GC.ROUTE_PATH_CLO_REPORT_LIST,
          },
        }),
      },
      {
        Component: CloItemsComponent,
        path: GC.ROUTE_PATH_CLO_ITEM_LIST,
        loader: () => dispatch({
          payload: {
            reportType: GC.CLO_ITEM_REPORT,
            pathname: GC.ROUTE_PATH_CLO_ITEM_LIST,
          },
          type: GC.VISIT_CLO_ITEM_LIST_PAGE,
        }),
      },
      {
        Component: LoadListComponent,
        path: GC.ROUTE_PATH_TEL_REPORT_LIST,
        loader: () => dispatch({
          type: GC.VISIT_TEL_LIST_PAGE,
          payload: {
            reportType: GC.TEL_REPORT,
            pathname: GC.ROUTE_PATH_TEL_REPORT_LIST,
          },
        }),
      },
      {
        Component: RouteListComponent,
        path: GC.ROUTE_PATH_ROUTE_REPORT_LIST,
        loader: () => dispatch({
          type: GC.VISIT_ROUTE_REPORT_LIST_PAGE,
          payload: {
            reportType: GC.ROUTE_REPORT,
            pathname: GC.ROUTE_PATH_ROUTE_REPORT_LIST,
          },
        }),
      },
      {
        loader: () => dispatch({
          payload: {
            route: GC.ROUTE_PATH_ROUTE_BUILDER,
          },
          type: GC.VISIT_ROUTE_BUILDER_V2_PAGE,
        }),
        path: GC.ROUTE_PATH_ROUTE_BUILDER,
        lazy: lazyWithRetry(() => import('./features/dispatch-planner-v2')),
      },
      {
        path: GC.ROUTE_PATH_NORMALIZE_CURRENCY_LIST,
        lazy: lazyWithRetry(() => import('./features/normalize-currency-list')),
        loader: () => dispatch({
          type: GC.VISIT_NORMALIZE_CURRENCY_LIST_PAGE,
          payload: {
            pathname: GC.ROUTE_PATH_NORMALIZE_CURRENCY_LIST,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_AVERAGE_FUEL_PRICE,
        lazy: lazyWithRetry(() => import('./features/average-fuel-price')),
        loader: () => dispatch({
          type: GC.VISIT_AVERAGE_FUEL_PRICE_PAGE,
          payload: {
            pathname: GC.ROUTE_PATH_AVERAGE_FUEL_PRICE,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_FLEET_ACTIVITY_REPORT,
        lazy: lazyWithRetry(() => import('./features/analytics/fleet-activity')),
        loader: () => dispatch({
          type: GC.VISIT_FLEET_ACTIVITY_REPORT_PAGE,
          payload: {
            pathname: GC.ROUTE_PATH_FLEET_ACTIVITY_REPORT,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_WEEKLY_RPM_BY_REGION_REPORT,
        lazy: lazyWithRetry(() => import('./features/analytics/weekly-rpm-by-region')),
        loader: () => dispatch({
          type: GC.VISIT_WEEKLY_RPM_BY_REGION_REPORT_PAGE,
          payload: {
            pathname: GC.ROUTE_PATH_WEEKLY_RPM_BY_REGION_REPORT,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_LANE_RPM_REPORT,
        lazy: lazyWithRetry(() => import('./features/analytics/lane-rpm')),
        loader: () => dispatch({
          type: GC.VISIT_LANE_RPM_REPORT_PAGE,
          payload: {
            pathname: GC.ROUTE_PATH_LANE_RPM_REPORT,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_DISPATCH_GROUP,
        lazy: lazyWithRetry(() => import('./features/dispatch-group')),
        loader: () => dispatch({
          type: GC.VISIT_DISPATCH_GROUP_PAGE,
          payload: {
            pathname: GC.ROUTE_PATH_DISPATCH_GROUP,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_CLO_RATE_CHARGE_REPORT,
        lazy: lazyWithRetry(() => import('./features/charge-report')),
        loader: () => dispatch({
          type: GC.VISIT_CLO_RATE_CHARGE_REPORT_PAGE,
          payload: {
            reportType: GC.CLO_RATE_CHARGE_REPORT,
            pathname: GC.ROUTE_PATH_CLO_RATE_CHARGE_REPORT,
            checkVisitPage: GC.CHECK_VISIT_CLO_RATE_CHARGE_REPORT_PAGE,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_TEL_RATE_CHARGE_REPORT,
        lazy: lazyWithRetry(() => import('./features/charge-report')),
        loader: () => dispatch({
          type: GC.VISIT_TEL_RATE_CHARGE_REPORT_PAGE,
          payload: {
            reportType: GC.TEL_RATE_CHARGE_REPORT,
            pathname: GC.ROUTE_PATH_TEL_RATE_CHARGE_REPORT,
            checkVisitPage: GC.CHECK_VISIT_TEL_RATE_CHARGE_REPORT_PAGE,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_TASK_MANAGEMENT_BOARD,
        lazy: lazyWithRetry(() => import('./features/task-management/board')),
        loader: () => dispatch({
          type: GC.VISIT_TASK_MANAGEMENT_PAGE,
          payload: {
            type: 'board',
            pathname: GC.ROUTE_PATH_TASK_MANAGEMENT_BOARD,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_TASK_MANAGEMENT_REPORT,
        lazy: lazyWithRetry(() => import('./features/task-management/report')),
        loader: () => dispatch({
          type: GC.VISIT_TASK_MANAGEMENT_PAGE,
          payload: {
            reportType: GC.TASK_MANAGEMENT_REPORT,
            pathname: GC.ROUTE_PATH_TASK_MANAGEMENT_REPORT,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_TASK_MANAGEMENT_BOARD_LIST,
        lazy: lazyWithRetry(() => import('./features/task-management/board-list')),
        loader: () => dispatch({
          type: GC.VISIT_TASK_MANAGEMENT_PAGE,
          payload: {
            type: 'boardList',
            pathname: GC.ROUTE_PATH_TASK_MANAGEMENT_BOARD,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_CLAIM_MANAGEMENT_LIST,
        lazy: lazyWithRetry(() => import('./features/claim-management/report')),
        loader: () => dispatch({
          type: GC.VISIT_CLAIM_MANAGEMENT_LIST_PAGE,
          payload: {
            reportType: GC.CLAIM_REPORT,
            pathname: GC.ROUTE_PATH_CLAIM_MANAGEMENT_LIST,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_CLAIM_MANAGEMENT_CLAIM_DETAILS,
        lazy: lazyWithRetry(() => import('./features/claim-management/details')),
        loader: ({ params }) => dispatch({
          type: GC.VISIT_CLAIM_MANAGEMENT_CLAIM_DETAILS_PAGE,
          payload: {
            ...params,
            pathname: GC.ROUTE_PATH_CLAIM_MANAGEMENT_CLAIM_DETAILS,
          },
        }),
      },
      // fleet
      {
        element: (
          <ListWrapper>
            <Outlet />
          </ListWrapper>
        ),
        children: [
          {
            path: GC.ROUTE_PATH_FLEET_MAP,
            lazy: lazyWithRetry(() => import('./features/fleet-map')),
            loader: () => dispatch({
              type: GC.VISIT_ANALYTIC_MAPS_PAGE,
              payload: {
                pathname: GC.ROUTE_PATH_FLEET_MAP,
              },
            }),
          },
          {
            Component: FleetList,
            path: GC.ROUTE_PATH_FLEET_DRIVERS_LIST,
            loader: () => dispatch({
              type: GC.VISIT_FLEET_DRIVER_LIST_PAGE,
              payload: {
                reportType: GC.FLEET_DRIVER_REPORT,
                pathname: GC.ROUTE_PATH_FLEET_DRIVERS_LIST,
                checkVisitPageType: GC.CHECK_VISIT_FLEET_DRIVERS_LIST_PAGE,
              },
            }),
          },
          {
            element: <div />,
            path: GC.ROUTE_PATH_FLEET_DRIVERS_EDIT,
            loader: ({ params: { guid } = {} }) => {
              throw redirect(routesMap.fleetDriverProfile(guid));
            },
          },
          {
            Component: FleetList,
            path: GC.ROUTE_PATH_FLEET_TRAILERS_LIST,
            loader: () => dispatch({
              type: GC.VISIT_FLEET_TRAILERS_LIST_PAGE,
              payload: {
                reportType: GC.FLEET_TRAILER_REPORT,
                pathname: GC.ROUTE_PATH_FLEET_TRAILERS_LIST,
                checkVisitPageType: GC.CHECK_VISIT_FLEET_TRAILERS_LIST_PAGE,
              },
            }),
          },
          {
            element: <div />,
            path: GC.ROUTE_PATH_FLEET_TRAILERS_EDIT,
            loader: ({ params: { guid } = {} }) => {
              throw redirect(routesMap.fleetTrailerProfile(guid));
            },
          },
          {
            Component: FleetList,
            path: GC.ROUTE_PATH_FLEET_TRUCKS_LIST,
            loader: () => dispatch({
              type: GC.VISIT_FLEET_TRUCKS_LIST_PAGE,
              payload: {
                reportType: GC.FLEET_TRUCK_REPORT,
                pathname: GC.ROUTE_PATH_FLEET_TRUCKS_LIST,
                checkVisitPageType: GC.CHECK_VISIT_FLEET_TRUCKS_LIST_PAGE,
              },
            }),
          },
          {
            element: <div />,
            path: GC.ROUTE_PATH_FLEET_TRUCKS_EDIT,
            loader: ({ params: { guid } = {} }) => {
              throw redirect(routesMap.fleetTruckProfile(guid));
            },
          },
          {
            Component: FleetList,
            path: GC.ROUTE_PATH_FLEET_VENDOR_LIST,
            loader: () => dispatch({
              type: GC.VISIT_FLEET_VENDOR_LIST_PAGE,
              payload: {
                reportType: GC.FLEET_VENDOR_REPORT,
                pathname: GC.ROUTE_PATH_FLEET_VENDOR_LIST,
                checkVisitPageType: GC.CHECK_VISIT_FLEET_VENDOR_LIST_PAGE,
              },
            }),
          },
          {
            element: <div />,
            path: GC.ROUTE_PATH_FLEET_VENDOR_EDIT,
            loader: ({ params: { guid } = {} }) => {
              throw redirect(routesMap.fleetVendorProfile(guid));
            },
          },
          {
            Component: FleetList,
            path: GC.ROUTE_PATH_FLEET_EQUIPMENT_SERVICE_LIST,
            loader: () => dispatch({
              type: GC.VISIT_FLEET_EQUIPMENT_SERVICE_LIST_PAGE,
              payload: {
                reportType: GC.FLEET_EQUIPMENT_SERVICE_REPORT,
                pathname: GC.ROUTE_PATH_FLEET_EQUIPMENT_SERVICE_LIST,
                checkVisitPageType: GC.CHECK_VISIT_FLEET_EQUIPMENT_SERVICE_LIST_PAGE,
              },
            }),
          },
          {
            path: GC.ROUTE_PATH_FLEET_EQUIPMENT_SERVICE_INVOICE_LIST,
            lazy: lazyWithRetry(async () => {
              const { default: Component } = await import('./features/equipment-service-invoice');

              return { Component };
            }),
            loader: () => dispatch({
              type: GC.VISIT_EQUIPMENT_SERVICE_INVOICE_PAGE,
              payload: {
                reportType: GC.FLEET_EQUIPMENT_SERVICE_INVOICE_REPORT,
                pathname: GC.ROUTE_PATH_FLEET_EQUIPMENT_SERVICE_INVOICE_LIST,
                checkVisitPageType: GC.CHECK_VISIT_EQUIPMENT_SERVICE_INVOICE_PAGE,
              },
            }),
          },
          {
            Component: FleetList,
            path: GC.ROUTE_PATH_FLEET_SHARED_COMPONENT_LIST,
            loader: () => dispatch({
              type: GC.VISIT_FLEET_SHARED_COMPONENT_LIST_PAGE,
              payload: {
                reportType: GC.FLEET_SHARED_COMPONENT_REPORT,
                pathname: GC.ROUTE_PATH_FLEET_SHARED_COMPONENT_LIST,
                checkVisitPageType: GC.CHECK_VISIT_FLEET_SHARED_COMPONENT_LIST_PAGE,
              },
            }),
          },
          {
            Component: FleetList,
            path: GC.ROUTE_PATH_FLEET_DRIVER_PAYROLL_ACCESSORIAL_LIST,
            loader: () => dispatch({
              type: GC.VISIT_FLEET_DRIVER_PAYROLL_ACCESSORIAL_LIST_PAGE,
              payload: {
                reportType: GC.FLEET_DRIVER_PAYROLL_ASSESSORIAL_REPORT,
                pathname: GC.ROUTE_PATH_FLEET_DRIVER_PAYROLL_ACCESSORIAL_LIST,
                checkVisitPageType: GC.CHECK_VISIT_FLEET_DRIVER_PAYROLL_ACCESSORIAL_LIST_PAGE,
              },
            }),
          },
          {
            Component: FleetList,
            path: GC.ROUTE_PATH_FLEET_VENDOR_PAYROLL_ACCESSORIAL_LIST,
            loader: () => dispatch({
              type: GC.VISIT_FLEET_VENDOR_PAYROLL_ACCESSORIAL_LIST_PAGE,
              payload: {
                reportType: GC.FLEET_VENDOR_PAYROLL_ASSESSORIAL_REPORT,
                pathname: GC.ROUTE_PATH_FLEET_VENDOR_PAYROLL_ACCESSORIAL_LIST,
                checkVisitPageType: GC.CHECK_VISIT_FLEET_VENDOR_PAYROLL_ACCESSORIAL_LIST_PAGE,
              },
            }),
          },
          {
            Component: FleetList,
            path: GC.ROUTE_PATH_FLEET_SERVICE_ISSUES_LIST,
            loader: () => dispatch({
              type: GC.VISIT_FLEET_SERVICE_ISSUES_LIST_PAGE,
              payload: {
                reportType: GC.FLEET_SERVICE_ISSUE_REPORT,
                pathname: GC.ROUTE_PATH_FLEET_SERVICE_ISSUES_LIST,
                checkVisitPageType: GC.CHECK_VISIT_FLEET_SERVICE_ISSUES_LIST_PAGE,
              },
            }),
          },
          {
            path: GC.ROUTE_PATH_GEO_FENCING_LOCATION,
            lazy: lazyWithRetry(() => import('./features/geo-fencing-location')),
            loader: () => dispatch({
              type: GC.VISIT_GEO_FENCING_LOCATION_PAGE,
              payload: {
                pathname: GC.ROUTE_PATH_GEO_FENCING_LOCATION,
              },
            }),
          },
          {
            Component: FleetList,
            path: GC.ROUTE_PATH_FLEET_DRIVER_EXPENSES_LIST,
            loader: () => dispatch({
              type: GC.VISIT_FLEET_DRIVER_EXPENSES_LIST_PAGE,
              payload: {
                reportType: GC.FLEET_DRIVER_EXPENSE_REPORT,
                pathname: GC.ROUTE_PATH_FLEET_DRIVER_EXPENSES_LIST,
                checkVisitPageType: GC.CHECK_VISIT_FLEET_DRIVER_EXPENSES_LIST_PAGE,
              },
            }),
          },
        ],
      },
      {
        path: GC.ROUTE_PATH_FUEL_CARDS,
        Component: FuelCardListComponent,
        loader: () => dispatch({
          type: GC.VISIT_FUEL_CARDS_PAGE,
          payload: {
            reportType: GC.FUEL_CARD_REPORT,
            pathname: GC.ROUTE_PATH_FUEL_CARDS,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_TOLL_CHARGES,
        lazy: lazyWithRetry(() => import('./features/toll-charges')),
        loader: () => dispatch({
          type: GC.VISIT_TOLL_CHARGES_PAGE,
          payload: {
            reportType: GC.TOLL_CHARGE_REPORT,
            pathname: GC.ROUTE_PATH_TOLL_CHARGES,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_IFTA_REPORT,
        lazy: lazyWithRetry(() => import('./features/ifta-report')),
        loader: () => dispatch({
          type: GC.VISIT_IFTA_REPORT_PAGE,
          payload: {
            reportType: GC.IFTA_REPORT,
            pathname: GC.ROUTE_PATH_IFTA_REPORT,
          },
        }),
      },
      // fleet-profile
      {
        path: GC.ROUTE_PATH_FLEET_PROFILE_DRIVER,
        lazy: lazyWithRetry(() => import('./features/fleet-profile/driver')),
        loader: ({ params }) => dispatch({
          type: GC.VISIT_FLEET_PROFILE_DRIVER_PAGE,
          payload: {
            ...params,
            route: GC.ROUTE_PATH_FLEET_PROFILE_DRIVER,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_FLEET_PROFILE_VENDOR,
        lazy: lazyWithRetry(() => import('./features/fleet-profile/vendor')),
        loader: ({ params }) => dispatch({
          type: GC.VISIT_FLEET_PROFILE_VENDOR_PAGE,
          payload: {
            ...params,
            route: GC.ROUTE_PATH_FLEET_PROFILE_VENDOR,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_FLEET_PROFILE_TRUCK,
        lazy: lazyWithRetry(() => import('./features/fleet-profile/truck')),
        loader: ({ params }) => dispatch({
          type: GC.VISIT_FLEET_PROFILE_TRUCK_PAGE,
          payload: {
            ...params,
            route: GC.ROUTE_PATH_FLEET_PROFILE_TRUCK,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_FLEET_PROFILE_TRAILER,
        lazy: lazyWithRetry(() => import('./features/fleet-profile/trailer')),
        loader: ({ params }) => dispatch({
          type: GC.VISIT_FLEET_PROFILE_TRAILER_PAGE,
          payload: {
            ...params,
            route: GC.ROUTE_PATH_FLEET_PROFILE_TRAILER,
          },
        }),
      },
      // background processes
      {
        path: GC.ROUTE_PATH_CARRIER_RATING_REPORT,
        lazy: lazyWithRetry(() => import('./features/carrier-rating-report')),
        loader: () => dispatch({
          type: GC.VISIT_CARRIER_RATING_REPORT_LIST_PAGE,
          payload: {
            pathname: GC.ROUTE_PATH_CARRIER_RATING_REPORT,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_IMPORT_REPORT,
        lazy: lazyWithRetry(() => import('./features/import-report')),
        loader: () => dispatch({
          type: GC.VISIT_IMPORT_REPORT_PAGE,
          payload: {
            pathname: GC.ROUTE_PATH_IMPORT_REPORT,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_RATE_SHOP,
        lazy: lazyWithRetry(() => import('./features/rate-shop')),
        loader: () => dispatch({
          type: GC.VISIT_RATE_SHOP_PAGE,
          payload: {
            pathname: GC.VISIT_RATE_SHOP_PAGE,
            reportType: GC.EXCEL_RATING_RESULT_REPORT,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_ROUTE_MASS_CREATE_REPORT,
        lazy: lazyWithRetry(() => import('./features/route-mass-create-report')),
        loader: () => dispatch({
          type: GC.VISIT_ROUTE_MASS_CREATE_REPORT_PAGE,
          payload: {
            pathname: GC.ROUTE_PATH_ROUTE_MASS_CREATE_REPORT,
          },
        }),
      },
      // configs
      {
        path: GC.ROUTE_PATH_CONFIG_PASSWORD,
        lazy: lazyWithRetry(() => import('./features/configurations/config-pages/password')),
        loader: () => dispatch({
          type: GC.VISIT_CONFIG_PASSWORD_PAGE,
          payload: GC.ROUTE_PATH_CONFIG_PASSWORD,
        }),
      },
      {
        path: GC.ROUTE_PATH_CONFIG_TEL,
        lazy: lazyWithRetry(() => import('./features/configurations/config-pages/tel')),
        loader: () => dispatch({
          type: GC.VISIT_CONFIG_TEL_PAGE,
          payload: GC.ROUTE_PATH_CONFIG_TEL,
        }),
      },
      {
        path: GC.ROUTE_PATH_CONFIG_CLO,
        lazy: lazyWithRetry(() => import('./features/configurations/config-pages/clo')),
        loader: () => dispatch({
          type: GC.VISIT_CONFIG_CLO_PAGE,
          payload: GC.ROUTE_PATH_CONFIG_CLO,
        }),
      },
      {
        path: GC.ROUTE_PATH_CONFIG_GENERAL,
        lazy: lazyWithRetry(() => import('./features/configurations/config-pages/general')),
        loader: () => dispatch({
          type: GC.VISIT_CONFIG_GENERAL_PAGE,
          payload: GC.ROUTE_PATH_CONFIG_GENERAL,
        }),
      },
      {
        path: GC.ROUTE_PATH_CONFIG_OPTIONAL,
        lazy: lazyWithRetry(() => import('./features/configurations/config-pages/optional')),
        loader: () => dispatch({
          type: GC.VISIT_CONFIG_OPTIONAL_PAGE,
          payload: GC.ROUTE_PATH_CONFIG_OPTIONAL,
        }),
      },
      {
        path: GC.ROUTE_PATH_CONFIG_TEMPLATES,
        lazy: lazyWithRetry(() => import('./features/configurations/config-pages/templates')),
        loader: () => dispatch({
          type: GC.VISIT_CONFIG_TEMPLATES_PAGE,
          payload: GC.ROUTE_PATH_CONFIG_TEMPLATES,
        }),
      },
      {
        path: GC.ROUTE_PATH_CONFIG_DRIVER,
        lazy: lazyWithRetry(() => import('./features/configurations/config-pages/driver')),
        loader: () => dispatch({
          type: GC.VISIT_CONFIG_DRIVER_PAGE,
          payload: GC.ROUTE_PATH_CONFIG_DRIVER,
        }),
      },
      {
        path: GC.ROUTE_PATH_CONFIG_FLEET_GENERAL,
        lazy: lazyWithRetry(() => import('./features/configurations/config-pages/fleet-general')),
        loader: () => dispatch({
          type: GC.VISIT_CONFIG_FLEET_GENERAL_PAGE,
          payload: GC.ROUTE_PATH_CONFIG_FLEET_GENERAL,
        }),
      },
      {
        path: GC.ROUTE_PATH_CONFIG_TRAILER,
        lazy: lazyWithRetry(() => import('./features/configurations/config-pages/trailer')),
        loader: () => dispatch({
          type: GC.VISIT_CONFIG_TRAILER_PAGE,
          payload: GC.ROUTE_PATH_CONFIG_TRAILER,
        }),
      },
      {
        path: GC.ROUTE_PATH_CONFIG_TRUCK,
        lazy: lazyWithRetry(() => import('./features/configurations/config-pages/truck')),
        loader: () => dispatch({
          type: GC.VISIT_CONFIG_TRUCK_PAGE,
          payload: GC.ROUTE_PATH_CONFIG_TRUCK,
        }),
      },
      {
        path: GC.ROUTE_PATH_CONFIG_INVOICES,
        lazy: lazyWithRetry(() => import('./features/configurations/config-pages/invoice')),
        loader: () => dispatch({
          type: GC.VISIT_CONFIG_INVOICES_PAGE,
          payload: GC.ROUTE_PATH_CONFIG_INVOICES,
        }),
      },
      {
        path: GC.ROUTE_PATH_CONFIG_RATING,
        lazy: lazyWithRetry(() => import('./features/configurations/config-pages/rating')),
        loader: () => dispatch({
          type: GC.VISIT_CONFIG_RATING_PAGE,
          payload: GC.ROUTE_PATH_CONFIG_RATING,
        }),
      },
      {
        path: GC.ROUTE_PATH_CONFIG_COMMUNICATION,
        lazy: lazyWithRetry(() => import('./features/configurations/config-pages/communication')),
        loader: () => dispatch({
          type: GC.VISIT_CONFIG_COMMUNICATION_PAGE,
          payload: GC.ROUTE_PATH_CONFIG_COMMUNICATION,
        }),
      },
      {
        path: GC.ROUTE_PATH_CONFIG_CARRIER,
        lazy: lazyWithRetry(() => import('./features/configurations/config-pages/carrier')),
        loader: () => dispatch({
          type: GC.VISIT_CONFIG_CARRIER_PAGE,
          payload: GC.ROUTE_PATH_CONFIG_CARRIER,
        }),
      },
      {
        path: GC.ROUTE_PATH_CONFIG_SERVICE_VENDOR,
        lazy: lazyWithRetry(() => import('./features/configurations/config-pages/service-vendor')),
        loader: () => dispatch({
          type: GC.VISIT_CONFIG_SERVICE_VENDOR_PAGE,
          payload: GC.ROUTE_PATH_CONFIG_SERVICE_VENDOR,
        }),
      },
      {
        path: GC.ROUTE_PATH_CONFIG_INTEGRATION,
        lazy: lazyWithRetry(() => import('./features/configurations/config-pages/integration')),
        loader: () => dispatch({
          type: GC.VISIT_CONFIG_INTEGRATION_PAGE,
          payload: GC.ROUTE_PATH_CONFIG_INTEGRATION,
        }),
      },
      {
        path: GC.ROUTE_PATH_CONFIG_UI,
        lazy: lazyWithRetry(() => import('./features/configurations/config-pages/ui')),
        loader: () => dispatch({
          type: GC.VISIT_CONFIG_UI_PAGE,
          payload: GC.ROUTE_PATH_CONFIG_UI,
        }),
      },
      {
        path: GC.ROUTE_PATH_CONFIG_MOBILEAPP,
        lazy: lazyWithRetry(() => import('./features/configurations/config-pages/mobileapp')),
        loader: () => dispatch({
          type: GC.VISIT_CONFIG_MOBILEAPP_PAGE,
          payload: GC.ROUTE_PATH_CONFIG_MOBILEAPP,
        }),
      },
      {
        path: GC.ROUTE_PATH_CONFIG_AUDIT,
        lazy: lazyWithRetry(() => import('./features/configurations/config-pages/audit')),
        loader: () => dispatch({
          type: GC.VISIT_CONFIG_AUDIT_PAGE,
          payload: GC.ROUTE_PATH_CONFIG_AUDIT,
        }),
      },
      {

        path: GC.ROUTE_PATH_CONFIG_TASK_MANAGEMENT,
        lazy: lazyWithRetry(() => import('./features/configurations/config-pages/task-management')),
        loader: () => dispatch({
          type: GC.VISIT_CONFIG_TASK_MANAGEMENT_PAGE,
          payload: GC.ROUTE_PATH_CONFIG_TASK_MANAGEMENT,
        }),
      },
      {
        path: GC.ROUTE_PATH_CONFIG_CLAIM_MANAGEMENT,
        lazy: lazyWithRetry(() => import('./features/configurations/config-pages/claim')),
        loader: () => dispatch({
          type: GC.VISIT_CONFIG_CLAIM_MANAGEMENT_PAGE,
          payload: GC.ROUTE_PATH_CONFIG_CLAIM_MANAGEMENT,
        }),
      },
      {
        path: GC.ROUTE_PATH_SPLASH_SCREEN_SETTINGS,
        lazy: lazyWithRetry(() => import('./features/styling/components/splash-screen')),
      },
      {
        path: GC.ROUTE_PATH_STYLING,
        lazy: lazyWithRetry(() => import('./features/styling')),
        loader: () => dispatch({
          type: GC.VISIT_STYLING_PAGE,
          payload: {
            pathname: GC.ROUTE_PATH_STYLING,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_DASHBOARDS_PAYROLL,
        lazy: lazyWithRetry(() => import('./features/dashboards-payroll')),
        loader: () => dispatch({
          type: GC.VISIT_DASHBOARDS_PAYROLL_PAGE,
          payload: {
            pathname: GC.ROUTE_PATH_DASHBOARDS_PAYROLL,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_NOTIFICATIONS,
        lazy: lazyWithRetry(() => import('./features/notification')),
        loader: () => dispatch({
          type: GC.VISIT_NOTIFICATION_LIST_PAGE,
          payload: {
            pathname: GC.ROUTE_PATH_NOTIFICATIONS,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_INTL,
        lazy: lazyWithRetry(() => import('./features/intl')),
        loader: () => dispatch({
          payload: GC.ROUTE_PATH_INTL,
          type: GC.VISIT_TRANSLATION_PAGE,
        }),
      },
      {
        path: GC.ROUTE_PATH_LOS_SUBCRIPTION_LIST,
        lazy: lazyWithRetry(() => import('./features/los-subscription')),
        loader: () => dispatch({
          type: GC.VISIT_ROUTE_LOS_SUBSCRIPTION_PAGE,
          payload: {
            reportType: GC.LOS_SUBSCRIPTION_REPORT,
            pathname: GC.ROUTE_PATH_LOS_SUBCRIPTION_LIST,
          },
        }),
      },
      {
        element: <OAuthPopup />,
        path: GC.ROUTE_PATH_OAUTH2_POPUP,
      },
      {
        path: GC.ROUTE_PATH_WORK_ORDER_LIST,
        lazy: lazyWithRetry(() => import('./features/work-order/report')),
        loader: () => dispatch({
          type: GC.VISIT_WORK_ORDER_LIST_PAGE,
          payload: {
            reportType: GC.WORK_ORDER_REPORT,
            pathname: GC.ROUTE_PATH_WORK_ORDER_LIST,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_COMPENSATION_GROUP,
        lazy: lazyWithRetry(async () => {
          const { default: Component } = await import('./features/compensation-group');

          return { Component };
        }),
        loader: () => dispatch({
          type: GC.VISIT_COMPENSATION_GROUP_PAGE,
          payload: {
            pathname: GC.ROUTE_PATH_COMPENSATION_GROUP,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_INSPECTIONS,
        lazy: lazyWithRetry(() => import('./features/inspections')),
        loader: () => dispatch({
          type: GC.VISIT_INSPECTION_REPORT_PAGE,
          payload: {
            reportType: GC.INSPECTION_REPORT,
            pathname: GC.ROUTE_PATH_INSPECTIONS,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_TRAILER_ACTIVITY_LIST,
        lazy: lazyWithRetry(() => import('./features/trailer-activity')),
        loader: () => dispatch({
          type: GC.VISIT_TRAILER_ACTIVITY_LIST_PAGE,
          payload: {
            pathname: GC.ROUTE_PATH_TRAILER_ACTIVITY_LIST,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_FLEET_DRIVER_DOCUMENTS,
        lazy: lazyWithRetry(() => import('./features/fleet-documents')),
        loader: () => dispatch({
          type: GC.VISIT_FLEET_DRIVER_DOCUMENTS_PAGE,
          payload: {
            reportType: GC.FLEET_DRIVER_DOCUMENT_VIEW_REPORT,
            pathname: GC.ROUTE_PATH_FLEET_DRIVER_DOCUMENTS,
            checkVisitPage: GC.CHECK_VISIT_FLEET_DRIVER_DOCUMENTS_PAGE,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_FLEET_TRUCK_DOCUMENTS,
        lazy: lazyWithRetry(() => import('./features/fleet-documents')),
        loader: () => dispatch({
          type: GC.VISIT_FLEET_TRUCK_DOCUMENTS_PAGE,
          payload: {
            reportType: GC.FLEET_TRUCK_DOCUMENT_VIEW_REPORT,
            pathname: GC.ROUTE_PATH_FLEET_TRUCK_DOCUMENTS,
            checkVisitPage: GC.CHECK_VISIT_FLEET_TRUCK_DOCUMENTS_PAGE,
          },
        }),
      },
      {
        path: GC.ROUTE_PATH_FLEET_TRAILER_DOCUMENTS,
        lazy: lazyWithRetry(() => import('./features/fleet-documents')),
        loader: () => dispatch({
          type: GC.VISIT_FLEET_TRAILER_DOCUMENTS_PAGE,
          payload: {
            reportType: GC.FLEET_TRAILER_DOCUMENT_VIEW_REPORT,
            pathname: GC.ROUTE_PATH_FLEET_TRAILER_DOCUMENTS,
            checkVisitPage: GC.CHECK_VISIT_FLEET_TRAILER_DOCUMENTS_PAGE,
          },
        }),
      },
      {
        Component: MasterAudit,
        path: GC.ROUTE_PATH_MASTER_AUDIT,
        loader: () => dispatch({
          type: GC.VISIT_MASTER_AUDIT_PAGE,
          payload: GC.ROUTE_PATH_MASTER_AUDIT,
        }),
      },
      // // Icons and components
      {
        path: GC.ROUTE_PATH_DEV_ICONS,
        lazy: lazyWithRetry(() => import('./features/icons')),
      },
      {
        path: GC.ROUTE_PATH_DEV_COMPONENTS,
        lazy: lazyWithRetry(() => import('./features/components-story')),
      },
    ],
  },
];

const router = (dispatch) => createBrowserRouter(routes2(dispatch));

const Router = () => {
  const dispatch = useDispatch();

  return <RouterProvider router={router(dispatch)} />;
};

export { Router };
